import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Users, History, Award, Gift, Shield, Flame } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { BADGE_DEFS } from "@/lib/storage";
import { prizeEmoji } from "@/lib/api";
import { GROUPS } from "@/data/mockData";
import { OffersStrip } from "@/components/OffersStrip";
import { weeklyCount, STREAK_TIERS, nextTier, resetLabel } from "@/lib/streak";

function catEmoji(id) {
  const map = { general: "🧠", sports: "🏆", music: "🎵", movies: "🎬", science: "🔬", history: "🏛️", local: "📍", lore: "👻" };
  return map[id] || "🍺";
}

export default function Profile() {
  const navigate = useNavigate();
  const { profile, logout } = useApp();

  const earned = new Set(profile.badges);
  const totalScore = profile.history.reduce((s, h) => s + h.score, 0);
  const streak = weeklyCount(profile.history);
  const upcoming = nextTier(streak);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between pt-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-amber-400 text-slate-950 flex items-center justify-center font-display font-black text-2xl shadow-[0_0_20px_rgba(245,158,11,0.4)]">
            {profile.name[0].toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-2xl font-black text-slate-50 leading-none">{profile.name}</h1>
            <p className="text-slate-400 text-sm mt-1">{profile.handle}</p>
          </div>
        </div>
        <button data-testid="profile-logout-btn" onClick={() => { logout(); navigate("/login"); }} className="text-slate-500 hover:text-red-400 p-2">
          <LogOut size={20} strokeWidth={2.5} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="bg-slate-900 rounded-2xl p-4 text-center border border-slate-800">
          <div className="font-display text-3xl font-black text-amber-400">{profile.history.length}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Quizzes</div>
        </div>
        <div className="bg-slate-900 rounded-2xl p-4 text-center border border-slate-800">
          <div className="font-display text-3xl font-black text-cyan-400">{totalScore * 100}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Points</div>
        </div>
        <div className="bg-slate-900 rounded-2xl p-4 text-center border border-slate-800">
          <div className="font-display text-3xl font-black text-pink-400">{profile.badges.length}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Badges</div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-orange-500/20 to-red-500/10 border border-orange-500/30 rounded-2xl p-5 mb-8" data-testid="weekly-streak">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Flame size={20} className="text-orange-400" />
            <h2 className="font-display text-lg font-black text-slate-50">Weekly Streak</h2>
          </div>
          <span className="text-[11px] font-bold text-orange-300/80">{resetLabel()}</span>
        </div>
        <div className="flex items-end gap-2 mb-4">
          <span data-testid="streak-count" className="font-display text-5xl font-black text-orange-400 leading-none">{streak}</span>
          <span className="text-slate-400 text-sm mb-1">quizzes since Sat 10pm</span>
        </div>
        <div className="flex gap-3">
          {STREAK_TIERS.map((t) => {
            const has = streak >= t.count;
            return (
              <div key={t.id} data-testid={`streak-tier-${t.id}`} className={`flex-1 rounded-xl p-2 text-center border ${has ? "bg-orange-500/20 border-orange-400/50" : "bg-slate-900 border-slate-800 opacity-50"}`}>
                <div className={`text-2xl ${has ? "" : "grayscale"}`}>{t.emoji}</div>
                <div className="text-[10px] font-bold text-slate-300 leading-tight mt-0.5">{t.name}</div>
                <div className="text-[9px] text-slate-500">{t.count} quizzes</div>
              </div>
            );
          })}
        </div>
        {upcoming && (
          <p className="text-xs text-slate-400 mt-3">{upcoming.count - streak} more for {upcoming.emoji} {upcoming.name}. Resets every Saturday 10pm.</p>
        )}
      </div>

      <OffersStrip title="Pub offers near you" />

      <h2 className="font-display text-lg font-black text-slate-50 mb-3 flex items-center gap-2">
        <Award size={18} className="text-amber-400" /> Badges
      </h2>
      <div className="grid grid-cols-3 gap-3 mb-8" data-testid="badge-grid">
        {BADGE_DEFS.map((b) => {
          const has = earned.has(b.id);
          return (
            <div
              key={b.id}
              data-testid={`badge-${b.id}`}
              className={`rounded-2xl p-3 text-center border ${has ? "bg-slate-900 border-amber-500/30" : "bg-slate-950 border-slate-800 opacity-40"}`}
            >
              <div className={`text-3xl mb-1 ${has ? "" : "grayscale"}`}>{b.emoji}</div>
              <div className="text-[10px] font-bold text-slate-300 leading-tight">{b.name}</div>
            </div>
          );
        })}
      </div>

      <h2 className="font-display text-lg font-black text-slate-50 mb-3 flex items-center gap-2">
        <Users size={18} className="text-pink-400" /> Groups
      </h2>
      <div className="mb-8" data-testid="profile-groups">
        {profile.groups.length === 0 ? (
          <p className="text-slate-500 text-sm">No groups yet. Join one from the Find tab!</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {profile.groups.map((g) => {
              const match = GROUPS.find((x) => x.name === g);
              return (
                <button
                  key={g}
                  data-testid={`profile-group-${match ? match.id : "x"}`}
                  onClick={() => match && navigate(`/group/${match.id}`)}
                  className="bg-slate-900 border border-slate-800 rounded-full px-4 py-2 text-sm font-bold text-slate-200 hover:border-pink-400/50 no-tap-highlight"
                >
                  {match ? match.emoji + " " : ""}{g}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <h2 className="font-display text-lg font-black text-slate-50 mb-3 flex items-center gap-2">
        <Gift size={18} className="text-amber-400" /> My Offers
      </h2>
      <div className="mb-8" data-testid="profile-offers">
        {(!profile.offers || profile.offers.length === 0) ? (
          <p className="text-slate-500 text-sm">No offers won yet. Play a partner venue quiz to win prizes!</p>
        ) : (
          <div className="space-y-3">
            {profile.offers.map((o) => (
              <div key={o.code} data-testid={`offer-${o.code}`} className="bg-slate-900 border border-amber-400/30 rounded-2xl p-4 flex items-center gap-3">
                <span className="text-2xl">{prizeEmoji(o.kind)}</span>
                <div className="flex-1">
                  <div className="font-bold text-slate-100 text-sm leading-tight">{o.title}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{o.venue} · code {o.code}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="font-display text-lg font-black text-slate-50 mb-3 flex items-center gap-2">
        <History size={18} className="text-cyan-400" /> Score History
      </h2>
      <div className="space-y-3" data-testid="history-list">
        {profile.history.length === 0 ? (
          <p className="text-slate-500 text-sm">Play your first quiz to build a history!</p>
        ) : (
          profile.history.map((h, i) => (
            <motion.div
              key={`${h.date}-${i}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-slate-900 rounded-2xl p-4 border border-slate-800 flex items-center gap-3"
            >
              <span className="text-2xl">{catEmoji(h.categoryId)}</span>
              <div className="flex-1">
                <div className="font-bold text-slate-100">{h.category}</div>
                <div className="text-slate-500 text-xs capitalize">{h.difficulty} · {h.venue}</div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-amber-400">{h.score}/{h.total}</div>
                <div className="text-slate-500 text-[10px]">{new Date(h.date).toLocaleDateString()}</div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <button
        data-testid="profile-admin-link"
        onClick={() => navigate("/admin/login")}
        className="mt-8 w-full flex items-center justify-center gap-2 text-slate-500 text-sm font-semibold py-3 border border-slate-800 rounded-2xl"
      >
        <Shield size={15} /> Partner venue? Open Admin Console
      </button>
    </div>
  );
}
